import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from 'layouts/AppLayout';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import { ROUTES } from 'config/navConfig';
import { useRole } from 'context/RoleContext';
import { ROLES } from 'config/roleConfig';
import {
  IC_STATES,
  IC_STATE_CONFIG,
  IC_PIPELINE_STEPS,
  getICStateConfig,
} from 'config/workflowStates';
import { MOCK_IC_SUBMISSIONS } from 'data/mockData';

// ─── State Chip ───────────────────────────────────────────────────────────────
function ICStateChip({ state }) {
  const cfg = getICStateConfig(state);
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold font-caption"
      style={{ color: cfg.color, background: cfg.bgColor }}
    >
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: cfg.color }} />
      {cfg.label}
    </span>
  );
}

// ─── Workflow Stepper ─────────────────────────────────────────────────────────
function ICWorkflowStepper({ currentState }) {
  const currentStep = IC_STATE_CONFIG[currentState]?.step ?? 0;
  const isTerminal  = IC_STATE_CONFIG[currentState]?.terminal ?? false;

  return (
    <div className="flex items-center gap-0">
      {IC_PIPELINE_STEPS.map((step, index) => {
        const cfg       = IC_STATE_CONFIG[step];
        const isDone    = currentStep > index || (isTerminal && currentStep >= index);
        const isActive  = currentStep === index && !isTerminal;
        const isLast    = index === IC_PIPELINE_STEPS.length - 1;

        return (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors"
                style={{
                  background: isDone || isActive ? 'var(--color-primary)' : 'var(--color-muted)',
                  color: isDone || isActive ? 'white' : 'var(--color-muted-foreground)',
                  border: isActive ? '2px solid var(--color-primary)' : '2px solid transparent',
                  boxShadow: isActive ? '0 0 0 3px rgba(59,76,76,0.15)' : 'none',
                }}
              >
                {isDone ? <Icon name="Check" size={14} /> : index + 1}
              </div>
              <span
                className="text-xs font-caption mt-1.5 text-center whitespace-nowrap"
                style={{
                  color: isActive
                    ? 'var(--color-primary)'
                    : isDone
                    ? 'var(--color-foreground)'
                    : 'var(--color-muted-foreground)',
                  fontWeight: isActive ? 600 : 400,
                  fontSize: '0.7rem',
                }}
              >
                {cfg.label}
              </span>
            </div>
            {!isLast && (
              <div
                className="h-0.5 flex-1 mx-1 mb-5"
                style={{
                  background: currentStep > index ? 'var(--color-primary)' : 'var(--color-border)',
                }}
              />
            )}
          </React.Fragment>
        );
      })}
      {/* Terminal state indicator */}
      {isTerminal && (
        <>
          <div
            className="h-0.5 flex-1 mx-1 mb-5"
            style={{ background: 'var(--color-border)' }}
          />
          <div className="flex flex-col items-center">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
              style={{
                background: IC_STATE_CONFIG[currentState]?.color ?? 'var(--color-muted)',
                color: 'white',
              }}
            >
              {currentState === IC_STATES.APPROVED || currentState === IC_STATES.APPROVED_WITH_CONDITIONS
                ? <Icon name="CheckCircle2" size={16} />
                : <Icon name="XCircle" size={16} />}
            </div>
            <span className="text-xs font-caption mt-1.5 text-center whitespace-nowrap" style={{ fontSize: '0.7rem', fontWeight: 600, color: IC_STATE_CONFIG[currentState]?.color }}>
              {IC_STATE_CONFIG[currentState]?.label}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Submission Row ───────────────────────────────────────────────────────────
function SubmissionRow({ sub, onSelect, isSelected }) {
  const cfg = getICStateConfig(sub.icState);
  return (
    <div
      className="flex items-center gap-4 px-5 py-4 cursor-pointer transition-colors rounded-lg"
      style={{
        background: isSelected ? 'rgba(59,76,76,0.05)' : 'transparent',
        borderLeft: isSelected ? '3px solid var(--color-primary)' : '3px solid transparent',
      }}
      onClick={() => onSelect(sub)}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-heading font-bold text-sm text-white"
        style={{ background: 'var(--color-primary)' }}
      >
        {sub.dealName.slice(0, 2).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-caption font-semibold text-sm text-foreground truncate">{sub.dealName}</p>
        <p className="font-caption text-xs text-muted-foreground">{sub.submittedBy} · {sub.submittedDate}</p>
      </div>
      <ICStateChip state={sub.icState} />
    </div>
  );
}

// ─── IC Vote Panel ────────────────────────────────────────────────────────────
function ICVotePanel({ submission, isICMember }) {
  const [voteState, setVoteState] = useState('idle'); // 'idle' | 'voting' | 'done'
  const [condition, setCondition] = useState('');

  if (!submission?.committeeMembers || submission.icState !== IC_STATES.UNDER_REVIEW) return null;

  const members = submission.committeeMembers;
  const approvedCount = members.filter((m) => m.vote === 'approved').length;
  const total = members.length;

  return (
    <div className="card px-6 py-5">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">
            Committee Vote
          </p>
          <p className="font-caption text-sm text-foreground">
            <span className="font-semibold">{approvedCount} of {total}</span> committee members have voted —{' '}
            <span style={{ color: '#059669' }}>{approvedCount} Approved</span>
          </p>
        </div>
      </div>

      {/* Member list */}
      <div className="mb-4">
        {members.map((member, i) => (
          <div
            key={member.initials}
            className="flex items-center gap-3 py-2.5"
            style={{ borderBottom: i < members.length - 1 ? '1px solid var(--color-border)' : 'none' }}
          >
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
              style={{ background: member.vote === 'approved' ? '#059669' : 'var(--color-muted-foreground)' }}
            >
              {member.initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-caption text-sm font-semibold text-foreground">{member.name}</p>
              <p className="font-caption text-muted-foreground" style={{ fontSize: '0.72rem' }}>{member.role}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {member.vote === 'approved' ? (
                <span
                  className="font-caption text-xs font-semibold px-2.5 py-1 inline-flex items-center gap-1"
                  style={{ color: '#059669', background: 'rgba(5,150,105,0.10)', borderRadius: '4px' }}
                >
                  <Icon name="Check" size={10} />Approved
                </span>
              ) : (
                <span
                  className="font-caption text-xs font-semibold px-2.5 py-1"
                  style={{ color: '#d97706', background: 'rgba(217,119,6,0.10)', borderRadius: '4px' }}
                >
                  Pending
                </span>
              )}
              {member.votedAt && (
                <span className="font-caption text-muted-foreground" style={{ fontSize: '0.7rem' }}>
                  {member.votedAt}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* IC Member vote action */}
      {isICMember && voteState === 'idle' && (
        <div className="pt-3 border-t border-border">
          <Button
            variant="default"
            size="sm"
            iconName="CheckCircle2"
            iconPosition="left"
            iconSize={14}
            onClick={() => setVoteState('voting')}
          >
            Cast Vote
          </Button>
        </div>
      )}

      {isICMember && voteState === 'voting' && (
        <div className="pt-3 border-t border-border space-y-3">
          <p className="font-caption text-sm font-semibold text-foreground">Cast your vote</p>
          <textarea
            className="w-full text-sm font-caption text-foreground border border-border rounded-lg p-3 resize-none focus:outline-none focus:ring-2"
            style={{ background: 'var(--color-muted)', minHeight: '72px', borderRadius: '6px' }}
            placeholder="Optional: enter conditions or notes for approval..."
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
          />
          <div className="flex gap-2">
            <Button
              variant="default"
              size="sm"
              iconName="CheckCircle2"
              iconPosition="left"
              iconSize={14}
              onClick={() => setVoteState('done')}
            >
              Approve
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setVoteState('idle')}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {isICMember && voteState === 'done' && (
        <div className="pt-3 border-t border-border flex items-center gap-2">
          <Icon name="CheckCircle2" size={16} color="#059669" />
          <span className="font-caption text-sm font-semibold" style={{ color: '#059669' }}>
            Vote recorded — {approvedCount + 1} of {total} approved
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ICSubmissionPage() {
  const navigate = useNavigate();
  const { role, can } = useRole();
  const [selected, setSelected] = useState(MOCK_IC_SUBMISSIONS[0]);
  const [filter, setFilter]     = useState('all');

  const isICMember      = role === ROLES.IC_MEMBER;
  const canApprove      = can('canApproveIC');
  const canSubmit       = can('canSubmitIC');

  const filtered = filter === 'all'
    ? MOCK_IC_SUBMISSIONS
    : MOCK_IC_SUBMISSIONS.filter((s) => s.icState === filter);

  const stateFilters = [
    { key: 'all',                         label: 'All' },
    { key: IC_STATES.UNDER_REVIEW,        label: 'Under Review' },
    { key: IC_STATES.SUBMITTED,           label: 'Submitted' },
    { key: IC_STATES.APPROVED,            label: 'Approved' },
    { key: IC_STATES.APPROVED_WITH_CONDITIONS, label: 'Approved w/ Conditions' },
    { key: IC_STATES.DRAFT,               label: 'Draft' },
    { key: IC_STATES.REJECTED,            label: 'Rejected' },
  ];

  const selectedCfg = selected ? getICStateConfig(selected.icState) : null;

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        {/* Page Header */}
        <div className="sticky top-0 px-6 md:px-10 lg:px-12 py-4 border-b border-border bg-background" style={{ zIndex: 'var(--z-nav)' }}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--color-primary)' }}
              >
                <Icon name="ClipboardCheck" size={20} color="white" />
              </div>
              <div>
                <h1 className="font-heading font-semibold text-foreground text-xl">IC Submissions</h1>
                <p className="font-caption text-xs text-muted-foreground">
                  {isICMember ? 'Investment Committee review queue' : 'Track deal submissions through the IC process'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {canSubmit && (
                <Button
                  variant="default"
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                  iconSize={14}
                  onClick={() => navigate(ROUTES.structuringHub)}
                >
                  New Submission
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                iconName="ChevronLeft"
                iconPosition="left"
                iconSize={14}
                onClick={() => navigate(ROUTES.pipeline)}
              >
                Pipeline
              </Button>
            </div>
          </div>
        </div>

        <div className="px-6 md:px-10 lg:px-12 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" style={{ minHeight: '600px' }}>

            {/* Left: Submissions List */}
            <div className="lg:col-span-1 card overflow-hidden flex flex-col">
              {/* Filter pills */}
              <div className="px-4 py-3 border-b border-border flex flex-wrap gap-1.5">
                {stateFilters.slice(0, 4).map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    className="px-2.5 py-1 rounded-full text-xs font-caption font-medium transition-all"
                    style={{
                      background: filter === f.key ? 'var(--color-primary)' : 'var(--color-muted)',
                      color:      filter === f.key ? 'white' : 'var(--color-muted-foreground)',
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              {/* List */}
              <div className="flex-1 overflow-y-auto py-2">
                {filtered.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                    <Icon name="Inbox" size={28} />
                    <p className="font-caption text-sm mt-2">No submissions</p>
                  </div>
                ) : (
                  filtered.map((sub) => (
                    <SubmissionRow
                      key={sub.id}
                      sub={sub}
                      onSelect={setSelected}
                      isSelected={selected?.id === sub.id}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Right: Submission Detail */}
            <div className="lg:col-span-2 flex flex-col gap-5">
              {selected ? (
                <>
                  {/* Workflow Stepper */}
                  <div className="card px-6 py-5">
                    <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                      Submission Progress
                    </p>
                    <ICWorkflowStepper currentState={selected.icState} />
                  </div>

                  {/* Committee Vote Panel */}
                  <ICVotePanel submission={selected} isICMember={isICMember} />

                  {/* Deal Summary */}
                  <div className="card px-6 py-5 flex-1">
                    <div className="flex items-start justify-between gap-4 mb-5">
                      <div>
                        <h2 className="font-heading font-semibold text-lg text-foreground">{selected.dealName}</h2>
                        <p className="font-caption text-sm text-muted-foreground mt-0.5">{selected.sector} · {selected.jurisdiction}</p>
                      </div>
                      <ICStateChip state={selected.icState} />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-5">
                      {[
                        { label: 'Deal Size',       value: selected.dealSize },
                        { label: 'Target IRR',      value: selected.targetIRR },
                        { label: 'Submitted By',    value: selected.submittedBy },
                        { label: 'Submitted Date',  value: selected.submittedDate },
                        { label: 'IC Date',         value: selected.icDate },
                        { label: 'Lead Analyst',    value: selected.leadAnalyst },
                      ].map((item) => (
                        <div key={item.label} className="p-3 rounded-lg" style={{ background: 'var(--color-muted)' }}>
                          <p className="font-caption text-xs text-muted-foreground">{item.label}</p>
                          <p className="font-caption text-sm font-semibold text-foreground mt-0.5">{item.value}</p>
                        </div>
                      ))}
                    </div>

                    {/* Risk Flags */}
                    {selected.riskFlags && (
                      <div
                        className="p-4 rounded-xl mb-5"
                        style={{ background: 'rgba(220,38,38,0.05)', border: '1px solid rgba(220,38,38,0.15)' }}
                      >
                        <p className="font-caption text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#dc2626' }}>
                          Risk Flags
                        </p>
                        <p className="font-caption text-sm text-foreground">{selected.riskFlags}</p>
                      </div>
                    )}

                    {/* IC Status Note */}
                    <div
                      className="p-4 rounded-xl mb-5"
                      style={{ background: selectedCfg?.bgColor, border: `1px solid ${selectedCfg?.color}30` }}
                    >
                      <div className="flex items-start gap-2">
                        <Icon name="Info" size={14} color={selectedCfg?.color} style={{ flexShrink: 0, marginTop: 2 }} />
                        <div>
                          <p className="font-caption text-sm font-semibold" style={{ color: selectedCfg?.color }}>
                            {selectedCfg?.label}
                          </p>
                          <p className="font-caption text-xs text-muted-foreground mt-0.5">
                            {selectedCfg?.description}
                          </p>
                          {selected.conditions && (
                            <p className="font-caption text-xs mt-2" style={{ color: selectedCfg?.color }}>
                              Conditions: {selected.conditions}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Post-approval cue: prompt SPV structuring */}
                    {(selected.icState === IC_STATES.APPROVED || selected.icState === IC_STATES.APPROVED_WITH_CONDITIONS) && canSubmit && (
                      <div
                        className="flex items-start gap-3 p-4 rounded-xl mb-4"
                        style={{ background: 'rgba(5,150,105,0.06)', border: '1px solid rgba(5,150,105,0.20)' }}
                      >
                        <Icon name="CheckCircle2" size={16} color="#059669" style={{ flexShrink: 0, marginTop: 1 }} />
                        <div className="flex-1 min-w-0">
                          <p className="font-caption text-sm font-semibold" style={{ color: '#059669' }}>IC Approved — Next step: Structure SPV</p>
                          <p className="font-caption text-xs text-muted-foreground mt-0.5">
                            This deal is approved for investment. Proceed to the SPV Dashboard to begin structuring and investor onboarding.
                          </p>
                          <div className="flex gap-2 mt-3">
                            <Button
                              variant="default"
                              size="sm"
                              iconName="Layers"
                              iconPosition="left"
                              iconSize={13}
                              onClick={() => navigate(ROUTES.spvDashboard)}
                            >
                              Open SPV Dashboard
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              iconName="UserPlus"
                              iconPosition="left"
                              iconSize={13}
                              onClick={() => navigate(ROUTES.investorOnboarding)}
                            >
                              Investor Onboarding
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      {canApprove && selected.icState === IC_STATES.UNDER_REVIEW && (
                        <>
                          <Button variant="default" size="sm" iconName="CheckCircle2" iconPosition="left" iconSize={14}>
                            Approve
                          </Button>
                          <Button variant="outline" size="sm" iconName="XCircle" iconPosition="left" iconSize={14}>
                            Reject
                          </Button>
                          <Button variant="outline" size="sm" iconName="MessageSquare" iconPosition="left" iconSize={14}>
                            Request Clarification
                          </Button>
                        </>
                      )}
                      {canSubmit && selected.icState === IC_STATES.DRAFT && (
                        <Button variant="default" size="sm" iconName="Send" iconPosition="left" iconSize={14}>
                          Submit to IC
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="FileText"
                        iconPosition="left"
                        iconSize={14}
                        onClick={() => navigate(ROUTES.dataRoom)}
                      >
                        View Supporting Docs
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="card flex items-center justify-center h-64 text-muted-foreground">
                  <div className="text-center">
                    <Icon name="ClipboardCheck" size={36} />
                    <p className="font-caption text-sm mt-2">Select a submission to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
